'use client';

import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import Slider, { Settings } from 'react-slick';

// import 'slick-carousel/slick/slick.css';
// import 'slick-carousel/slick/slick-theme.css';
import { ElementVisibilityChecker } from './ElementVisibilityChecker';

interface ISlickCarouselProps {
  /**
   * It use to render the children component for the carousel
   */
  children: React.ReactNode;
}

export interface ISlickCarouselRef {
  /**
   * It use to navigate the next slide
   */
  onGotoNextSlide: () => void;
  /**
   * It use to navigate the previous slide
   */
  onGotoPreviousSlide: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * It renders the slick carousel component which can auto slide and pause on
 * hover. It also have programmatically action control to the slide.
 */
function SlickCarouselComponent(
  { children }: Readonly<ISlickCarouselProps>,
  ref: React.Ref<ISlickCarouselRef>
) {
  const sliderRef = useRef<Slider>(null);

  /**
   * TODO: Make this configurable via props.
   */
  const settings: Settings = {
    autoplay: true,
    className: 'center',
    centerMode: true,
    speed: 500,
    pauseOnHover: true,
    slidesToShow: 4,
    infinite: true,
    responsive: [
      {
        breakpoint: 1366,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const onGotoNextSlide = useCallback(() => {
    sliderRef.current?.slickNext();
  }, [sliderRef]);

  const onGotoPreviousSlide = useCallback(() => {
    sliderRef.current?.slickPrev();
  }, [sliderRef]);

  useImperativeHandle(
    ref,
    () => ({
      onGotoNextSlide,
      onGotoPreviousSlide,
    }),
    [onGotoNextSlide, onGotoPreviousSlide]
  );

  const handleOnVisibleChange = (isVisible: boolean) => {
    if (isVisible) {
      sliderRef.current?.slickPlay();
    } else {
      sliderRef.current?.slickPause();
    }
  };

  return (
    <ElementVisibilityChecker
      className="slick-carousel-container"
      onVisibleChange={handleOnVisibleChange}
    >
      <Slider ref={sliderRef} {...settings}>
        {children}
      </Slider>
    </ElementVisibilityChecker>
  );
}

export const SlickCarousel = forwardRef(SlickCarouselComponent);
